// src/components/FeedbackForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
const FeedbackForm = () => {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: '',
    message: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Update form data when user is available
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Changing ${name} to ${value}`); // Debug state changes
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with:', formData); // Debug form data

    // Validation
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.type) {
      setError('Please select feedback type');
      return;
    }
    if (!formData.message.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      const response = await axiosInstance.post('/feedback/submit', formData);
      console.log('API response:', response.data); // Debug API response
      setSuccess('Feedback submitted successfully!');
      setError('');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('API error:', err.response); // Debug error
      setError(err.response?.data?.message || 'Failed to submit feedback');
      setSuccess('');
    }
  };

  return (
    <div className="p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Feedback Form</h1>
            <p className="text-slate-600 mt-1">Help us improve by sharing your thoughts</p>
          </div>

          {/* Feedback Card */}
          <div className="max-w-2xl mx-auto">
            <Card className="shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-t-lg">
                <CardTitle className="text-2xl text-white mb-2 flex items-center gap-3">
                  <MessageSquare className="w-7 h-7" />
                  We Value Your Feedback
                </CardTitle>
                <p className="text-slate-300">
                  Help us improve StockKhata by sharing your thoughts and suggestions
                </p>
              </CardHeader>
              
              <CardContent className="p-8">
                {/* Error/Success Messages */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div className="text-red-700">{error}</div>
                  </div>
                )}
                {success && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="text-green-700">{success}</div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-slate-900 bg-white"
                      placeholder="Enter your name"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-slate-900 bg-white"
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  {/* Type Field */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="type"
                          value="feedback"
                          checked={formData.type === 'feedback'}
                          onChange={handleChange}
                          className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                        />
                        <span className="text-slate-700 font-medium">Feedback</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="type"
                          value="feature"
                          checked={formData.type === 'feature'}
                          onChange={handleChange}
                          className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                        />
                        <span className="text-slate-700 font-medium">Feature Suggestion</span>
                      </label>
                    </div>
                  </div>

                  {/* Message Field */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-slate-900 bg-white resize-vertical"
                      placeholder="Tell us what you think... Share your suggestions, report issues, or let us know how we can improve StockKhata."
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02]"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Submit Feedback
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
    </div>
  );
};

export default FeedbackForm;